import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const LazyImage = ({ src, alt, className, style, zoomable = true, eager = false }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [inView, setInView] = useState(eager);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (eager) return; // skip IntersectionObserver — load immediately
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [eager]);

  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightboxOpen]);

  return (
    <>
      <div
        ref={imgRef}
        className={`relative overflow-hidden ${zoomable ? 'cursor-zoom-in' : ''} ${className || ''}`}
        style={style}
        onClick={() => zoomable && loaded && setLightboxOpen(true)}
      >
        {/* Stable placeholder + error fallback */}
        {!loaded && !errored && (
          <div className="absolute inset-0 bg-gray-200" />
        )}
        {errored && (
          <div className="absolute inset-0 bg-gradient-to-br from-aqua-100 to-aqua-200 flex items-center justify-center text-aqua-700 text-xs font-semibold text-center p-3">
            {alt || 'Image unavailable'}
          </div>
        )}
        {inView && !errored && (
          <img
            src={src}
            alt={alt}
            loading={eager ? 'eager' : 'lazy'}
            fetchpriority={eager ? 'high' : 'auto'}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            className={`w-full h-full object-cover transition-opacity duration-300 ease-out ${loaded ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
      </div>

      {/* Lightbox via Portal to avoid overflow clipping */}
      {lightboxOpen && createPortal(
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[90vh] object-contain rounded-lg touch-pinch-zoom"
            style={{ touchAction: 'pinch-zoom' }}
          />
          <p className="absolute bottom-6 left-0 right-0 text-center text-white/70 text-sm">{alt}</p>
        </div>,
        document.body
      )}
    </>
  );
};

export default LazyImage;
